'use client'

import AppIcon from "@/app/components/atomic/AppIcon"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import StarsRating from "@/app/components/atomic/StarsRating"
import CallbackModal from "@/app/components/pageModels/healthLanding/components/CallbackModal"
import {Products} from "@/app/enums/Products"
import {IWPContentPayload, TBreadcrumb} from "@/app/models/wp"
import Image from "next/image"
import {usePathname} from "next/navigation"
import React, {ReactNode, useState} from 'react'
import SectionTitle, {SectionTitleTheme} from "@/app/components/atomic/molecules/SectionTitle";
import {getThemeColors, ThemeCocoon} from "@/app/constants/theme";
import Breadcrumb from "@/app/components/atomic/molecules/Breadcrumb";
import {scrollToFirstSection} from "@/app/utils/scroll-to-section";
import {getProductName} from "@/app/utils/context-utils";
import {resolveHeroClaims} from "@/app/utils/hero-claims";

interface ImprovedHeaderProps {
    globaleNotation?: number
    reviewNumber?: number
    product?: ThemeCocoon
    callbackModalName?: string
    pageContent: IWPContentPayload
    isLanding?: boolean
    breadcrumb?: TBreadcrumb
    quickStartWidget?: ReactNode
    ctaText?: string
    centerReviewsOnMobile?: boolean
}

export default function ImprovedHeader({
                                           globaleNotation,
                                           reviewNumber,
                                           product,
                                           callbackModalName,
                                           pageContent,
                                           isLanding = false,
                                           breadcrumb,
                                           quickStartWidget,
                                           ctaText,
                                           centerReviewsOnMobile = false,
                                       }: ImprovedHeaderProps) {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const pathname = usePathname()
    const themeProduct = product ?? (pathname ? getProductName(pathname) : undefined)
    const title = pageContent.h1
    const improvedHeader = pageContent.improvedHeader

    // Extract token from URL pathname (used for health products to pass through CTA)
    const token = (() => {
        if (product !== Products.HEALTH || !pathname) return null
        const lastSegment = pathname.split('/').filter(Boolean).pop()
        if (lastSegment && lastSegment.length === 64 && /^[a-f0-9]+$/i.test(lastSegment)) {
            return lastSegment
        }
        return null
    })()

    const displayedInfos = {
        mainClaim: improvedHeader?.mainClaim || "Comparez, choisissez, c'est signé",
        subClaims: resolveHeroClaims(improvedHeader?.subClaims, [
            "L'assurance idéale en 3mn",
            "100% gratuit",
            "Comparateur le mieux noté du marché"
        ]),
        ctaText: ctaText || improvedHeader?.ctaText || "JE COMPARE",
        imgUrl: improvedHeader?.imageUrl || null,
        imgAlt: pageContent.title,
        isLinkTargetBlank: improvedHeader?.isLinkTargetBlank ?? false,
    }

    // Build the CTA URL with token if available for health products
    const getCtaUrl = () => {
        let baseUrl = improvedHeader?.ctaUrl || ""

        if (process.env.NEXT_PUBLIC_IS_PREPROD === 'true' && process.env.NEXT_PUBLIC_RM_NEXT_URL && process.env.NEXT_PUBLIC_RM_BASE_API) {
            baseUrl = baseUrl
                .replace('https://reassurez-moi.fr', process.env.NEXT_PUBLIC_RM_NEXT_URL)
                .replace('https://app.reassurez-moi.fr', process.env.NEXT_PUBLIC_RM_BASE_API)
        }

        // For health products, append token from current URL to the CTA URL
        if (product === Products.HEALTH && token && baseUrl) {
            // Check if URL already contains the token
            if (baseUrl.includes(token)) {
                return baseUrl
            }
            // Append token to the CTA URL
            return baseUrl.endsWith('/') ? `${baseUrl}${token}` : `${baseUrl}/${token}`
        }

        return baseUrl
    }

    const callbackButton = callbackModalName && (
        <AppButton
            wFull
            theme="whiteBlur"
            onClick={() => setIsModalOpen(true)}
            dataTest="callback-cta"
            size="sm"
        >
            <AppIcon iconClassName={"fa-phone"} size={"xs"} className="pr-2" />
            Être rappelé par un conseiller
        </AppButton>
    )

    return (
        <section className="w-full bg-neutral" aria-label="Présentation de l'offre">
            {isModalOpen && callbackModalName && product && (
                <CallbackModal
                    modalName={callbackModalName}
                    isOpen={true}
                    closeModal={() => setIsModalOpen(false)}
                    productType={product}
                    fullHeightMobile
                    modalTitle='Être rappelé par un conseiller'
                    fetchSlotsFromApi={product === Products.LOAN}
                />
            )}

            <div
                className={`max-w-7xl w-full mx-auto pt-[50px]
                            ${isLanding ? "pb-16 lg:pb-40" : ""}`}
            >
                {breadcrumb &&
                    <div className="px-6 lg:px-10 max-w-screen-md lg:max-w-max lg:m-0 mx-auto">
                        <Breadcrumb breadcrumb={breadcrumb} />
                    </div>
                }

                <div className="flex flex-col lg:flex-row lg:items-stretch">

                    <div className="flex flex-col max-w-screen-md w-full px-2 md:px-6 lg:ml-10 lg:px-0 gap-8 mx-auto lg:pr-10">
                        <div className="flex flex-col md:gap-8">
                            <SectionTitle
                                level="h1"
                                theme={getThemeColors(themeProduct).primary as SectionTitleTheme}
                                topText={{text: displayedInfos.mainClaim, icon: "fa-euro"}}
                            >
                                {title}
                            </SectionTitle>

                            {!quickStartWidget && improvedHeader?.imageUrl && (
                                <div className="relative w-full h-[200px] sm:h-[280px] md:h-[320px] lg:hidden rounded-xl overflow-hidden">
                                    <Image
                                        src={improvedHeader.imageUrl}
                                        alt={displayedInfos.imgAlt}
                                        className="object-cover"
                                        style={{objectPosition: `${improvedHeader.imagePosition ?? 50}% center`}}
                                        quality={87}
                                        priority
                                        fill
                                        sizes="(min-width: 1024px) 1px, (min-width: 768px) 768px, 100vw"
                                    />
                                </div>
                            )}
                        </div>

                        {!quickStartWidget && (
                            <div className="w-fit">
                                <ul className="flex flex-col gap-4">
                                    {displayedInfos.subClaims.map((item, index) => (
                                        <li
                                            key={index}
                                            className="text-rm-14 font-medium mt-rm-5 flex items-center"
                                        >
                                            <span className="flex items-center justify-center w-[20px] h-[20px] bg-deep-green rounded-full mr-2 shrink-0">
                                                <AppIcon
                                                    iconClassName="fa fa-check"
                                                    className="text-white text-rm-8"
                                                />
                                            </span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {quickStartWidget ? (
                            <div className="w-full flex flex-col gap-6 lg:pr-16">
                                {quickStartWidget}
                                {callbackButton}
                            </div>
                        ) : (
                        <div className="w-full flex gap-6 [&>*]:flex-1 flex-col md:flex-row">
                            {(improvedHeader?.isComparatorScroll ? (
                                <AppButton
                                    wFull
                                    dataTest={product === Products.HEALTH ? "health-compare-cta" : "loan-insurance-cta"}
                                    onClick={() => scrollToFirstSection(['pretto-journey', 'comparator-section', 'tool-section', 'cards-section'])}
                                >
                                    {displayedInfos.ctaText}
                                </AppButton>
                            ) : (
                                <AppButton
                                    wFull
                                    href={getCtaUrl()}
                                    target={displayedInfos.isLinkTargetBlank ? "_blank" : "_self"}
                                    dataTest={product === Products.HEALTH ? "health-compare-cta" : "loan-insurance-cta"}
                                >
                                    {displayedInfos.ctaText}
                                </AppButton>
                            ))
                            }

                            {callbackButton}
                        </div>
                        )}

                        {globaleNotation != null && reviewNumber != null && (
                            <div
                                className={`flex items-center gap-6 ${quickStartWidget ? 'justify-center lg:pr-16' : centerReviewsOnMobile ? 'justify-center lg:justify-start' : ''}`}
                            >
                                <div className="relative object-contain w-[65px] h-[23px]">
                                    <Image
                                        className={'object-contain'}
                                        src="/assets/health/companies/AV_COLOR.png"
                                        alt="avis vérifiés"
                                        fill
                                        sizes="10vw"
                                    />
                                </div>

                                <div className="flex gap-2 xxs:gap-6 flex-col xxs:flex-row h-full items-center">
                                    <StarsRating
                                        notation={globaleNotation}
                                        color="green"
                                        displayRatingNotation
                                        starsFontSize="text-rm-14"
                                        noteFontSize="text-rm-14"
                                    />

                                    <span className="text-rm-gray text-rm-14 font-light">
                                        {reviewNumber} avis
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {improvedHeader?.imageUrl && (
                        <div
                            className="relative hidden lg:block lg:flex-1 lg:min-w-[320px] 2xl:max-w-[500px] overflow-hidden lg:rounded-l-[50px] xl:rounded-[50px]"
                        >
                            <Image
                                src={improvedHeader.imageUrl}
                                alt={displayedInfos.imgAlt}
                                className="object-cover overflow-hidden"
                                style={{objectPosition: `${improvedHeader.imagePosition ?? 50}% center`}}
                                quality={87}
                                priority
                                fill
                                sizes="(min-width: 1024px) 500px, 1px"
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
